import type { QueryClient } from '@tanstack/react-query';
import {
  type GetAuditSummaryCommandOutput,
  type QuestionResponseDetails,
  type ResponseType,
  type SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';
import { auditSummaryPredicate } from './patchAuditCachePlumbing';
import { writeCachedResponseTokens } from './questionResponseTokens';
import { normalizeToTimestamp } from 'src/utils/dateUtils';

/**
 * Wire types for the `ListAuditQuestionResponses` legacy REST
 * endpoint, mirroring `src/types/helios/QuestionResponse.ts` but
 * co-located here so the polling merger doesn't reach across the
 * v2/legacy boundary. These describe what the SERVER returns for
 * the polled list — NOT the same shape as the SDK's
 * `QuestionResponseDetails` inside `useAuditSummaryQuery`'s cache.
 * The merger below translates between the two.
 */

export interface SubResponseFromWire {
  readonly subQuestionId: string;
  readonly status: string;
  readonly result?: string;
  readonly score?: number;
  readonly respondedBy: string;
  /** Epoch-millis string per the legacy REST wire shape. */
  readonly respondedAt: string;
  readonly comment?: string;
  readonly naReason?: string;
  readonly naReasonId?: string;
  readonly mediaIds?: readonly string[];
  readonly taskIds?: readonly string[];
  /**
   * Enhanced Response Types: the sub-question's data-collection type
   * and captured value. Absent `responseType` means legacy PASS_FAIL.
   * These live at the ENTRY level on the SDK read shape
   * (`SubQuestionResponseEntry`), not inside its `response` block.
   */
  readonly responseType?: string;
  readonly responseValue?: string;
}

export interface AuditQuestionItemResponse {
  readonly auditId: string;
  readonly businessUnitId: string;
  readonly questionId: string;
  readonly responseStatus: string;
  readonly responseResult?: string;
  readonly responseScore?: number;
  readonly respondedBy: string;
  /** Epoch-millis string per the legacy REST wire shape. */
  readonly respondedAt: string;
  /** When the row was last touched. Used for the top-level "did
   *  anything about this question change?" gate on the polling
   *  merger. */
  readonly lastUpdatedAt: string;
  readonly lastUpdatedBy: string;
  /**
   * Parent-level answer payload. OPTIONAL on the wire — omitted for a
   * question the auditor hasn't given a parent-level answer to.
   */
  readonly response?: {
    readonly comment?: string;
    readonly naReason?: string;
    readonly naReasonId?: string;
    readonly mediaIds?: readonly string[];
    readonly taskIds?: readonly string[];
  };
  /**
   * Sub-question answers. OPTIONAL on the wire — `ListAuditQuestionResponses`
   * omits the key entirely for a FLAT question (one with no
   * sub-questions), rather than sending an empty array.
   *
   * This was previously typed as required, which was simply wrong: it
   * crashed the perform-audit page with "can't access property length,
   * polledSubs is undefined" the moment the primary-table read returned
   * a flat question. Keep it optional so the compiler forces every
   * consumer to handle absence.
   */
  readonly subResponses?: readonly SubResponseFromWire[];
}

/**
 * One question entry in the SDK read shape, as the audit-summary
 * cache holds it. Both the polling merger and the perform-audit page
 * overlay operate on arrays of these.
 */
type SummaryQuestion = GetAuditSummaryCommandOutput['questions'][number];

/**
 * One-way merge of poll results (from `ListAuditQuestionResponses`)
 * into every matching `useAuditSummaryQuery` cache entry.
 *
 * Design constraints:
 *
 * 1. **Per-slot newest-wins.** Every field slot (parent-level, and
 *    each sub-response) is gated by its own `respondedAt` timestamp.
 *    If a client-side optimistic patch wrote a fresher timestamp
 *    than the poll returned, the local patch wins for that slot.
 *    This is the "async completion re-check intent" pattern —
 *    a stale poll can't clobber a save that landed while it was
 *    in flight.
 *
 * 2. **No-op when nothing changed.** If none of the polled items
 *    have a newer timestamp than the cache, the merger returns
 *    without touching the cache. React Query subscribers stay on
 *    the same reference and don't re-render. Matches V1's
 *    `pollQuestionResponsesApiThunk.fulfilled` reducer.
 *
 * 3. **No rollback.** Unlike optimistic patches, the poll is
 *    authoritative for whatever slot it wins. Callers don't need
 *    a snapshot array back.
 *
 * 4. **Definitions preserved.** The merger only touches
 *    `responseDetails.mainQuestionResponse` and
 *    `responseDetails.subQuestionResponses` — question definitions,
 *    templates, media metadata, and audit-level fields are left
 *    exactly as-is.
 */
export function mergePolledResponsesIntoSummaryCache(
  queryClient: QueryClient,
  auditId: string,
  items: readonly AuditQuestionItemResponse[],
): void {
  if (items.length === 0) return;

  // Sidecar tokens are written unconditionally — even when the poll
  // finds no summary-cache matches (e.g. a page that consumes the
  // polling data but doesn't hold the summary open). The mutation's
  // onMutate reads from here to enrich `expectedLastUpdatedAt`.
  writeCachedResponseTokens(
    queryClient,
    auditId,
    items.map((item) => [item.questionId, item.lastUpdatedAt] as const),
  );

  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  if (matchingQueries.length === 0) return;

  const itemsByQuestionId = new Map<string, AuditQuestionItemResponse>(
    items.map((item) => [item.questionId, item]),
  );

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = mergeIntoOne(cachedData, itemsByQuestionId);
    if (nextData !== cachedData) {
      queryClient.setQueryData(queryKey, nextData);
    }
  }
}

/**
 * Overlay primary-table response details onto a summary question
 * array — the single resolution every perform-audit consumer reads.
 *
 * The summary supplies the SKELETON (which questions the audit has,
 * their order and content) and the primary-table list supplies the
 * RESPONSES. Applying the overlay once, here, keeps the whole page on
 * one version of the truth: the question the auditor is answering, the
 * progress counter, the nav dots, and the close-audit gate all read
 * the same responses. Resolving per-consumer instead let the
 * interaction show a fresh answer while the progress strip still
 * showed the question unanswered.
 *
 * Gating is IDENTICAL to the polling merger's because it is literally
 * the same code — see design constraint #1 on
 * {@link mergePolledResponsesIntoSummaryCache}. That matters in three
 * ways a wholesale replacement would get wrong:
 *
 *   - A placeholder / never-answered wire row (`respondedAt` absent
 *     or 0) cannot blank a legitimate summary response.
 *   - A list read that resolves while a later save is in flight
 *     cannot clobber that save's optimistic patch, because the
 *     patched slot is not older than the row being applied.
 *   - Per-slot gating means a fresh parent comment and a stale sub
 *     answer in the same wire item are resolved independently.
 *
 * Questions with no row in `items` keep their summary `responseDetails`
 * (never answered, or the list read hasn't landed yet), so first paint
 * is unchanged and this degrades to today's behaviour when the primary
 * table read fails.
 *
 * Returns the input array BY REFERENCE when nothing changed, so
 * callers can memoise on it without re-running downstream derivations
 * on every refetch.
 *
 * Pure — no react, no cache access.
 */
export function overlayFreshResponsesOntoQuestions(
  questions: readonly SummaryQuestion[],
  items: readonly AuditQuestionItemResponse[] | undefined,
): readonly SummaryQuestion[] {
  if (!items || items.length === 0 || questions.length === 0) return questions;
  return overlayQuestions(
    questions,
    new Map(items.map((item) => [item.questionId, item])),
  );
}

/**
 * Per-question overlay shared by the page-level read
 * ({@link overlayFreshResponsesOntoQuestions}) and the cache merger
 * ({@link mergeIntoOne}). Both translate the same wire rows into the
 * same summary shape under the same newest-wins gate, so this exists
 * once rather than twice.
 *
 * Returns the input by reference when no question changed.
 */
function overlayQuestions(
  questions: readonly SummaryQuestion[],
  itemsByQuestionId: ReadonlyMap<string, AuditQuestionItemResponse>,
): readonly SummaryQuestion[] {
  let anyChanged = false;
  const next = questions.map((question) => {
    const questionId = question.questionDetails?.questionId;
    if (!questionId) return question;
    const item = itemsByQuestionId.get(questionId);
    if (!item) return question;
    const nextResponseDetails = mergeResponseDetails(question.responseDetails, item);
    if (nextResponseDetails === question.responseDetails) return question;
    anyChanged = true;
    return { ...question, responseDetails: nextResponseDetails };
  });
  return anyChanged ? next : questions;
}

/**
 * Return next cache shape for one summary entry, or the input by
 * reference if nothing changed. Reference-equal return is the signal
 * to the caller that no `setQueryData` write is needed.
 */
function mergeIntoOne(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  itemsByQuestionId: ReadonlyMap<string, AuditQuestionItemResponse>,
): GetAuditSummaryCommandOutput | undefined {
  if (!cachedData?.questions) return cachedData;
  const nextQuestions = overlayQuestions(cachedData.questions, itemsByQuestionId);
  if (nextQuestions === cachedData.questions) return cachedData;
  // Cast to mutable — same reason as `subQuestionResponses` below:
  // the SDK types this slot as a mutable array while we treat it as
  // readonly internally. Runtime shape is identical.
  return { ...cachedData, questions: nextQuestions as SummaryQuestion[] };
}

/**
 * Merge one polled item into an existing `responseDetails` blob.
 * Parent and sub slots are gated independently — see design
 * constraint #1.
 */
function mergeResponseDetails(
  previous: GetAuditSummaryCommandOutput['questions'][number]['responseDetails'],
  item: AuditQuestionItemResponse,
): GetAuditSummaryCommandOutput['questions'][number]['responseDetails'] {
  const nextMain = mergeParentResponse(previous?.mainQuestionResponse, item);
  const nextSubs = mergeSubResponses(
    previous?.subQuestionResponses ?? [],
    item.subResponses,
  );
  const mainChanged = nextMain !== previous?.mainQuestionResponse;
  const subsChanged = nextSubs !== previous?.subQuestionResponses;
  if (!mainChanged && !subsChanged) return previous;
  return {
    ...previous,
    mainQuestionResponse: nextMain ?? previous?.mainQuestionResponse,
    // Cast to mutable — the SDK types this slot as
    // `SubQuestionResponseEntry[]` even though we treat it as
    // readonly internally. Runtime shape is identical; the cast
    // only unbinds the compile-time readonly modifier.
    subQuestionResponses: nextSubs as SubQuestionResponseEntry[],
  };
}

/**
 * Newest-wins gate on the PARENT-level response. Returns the input
 * `previous` by reference when the poll's timestamp is not newer
 * than the cached timestamp — so structural sharing kicks in and no
 * subscriber re-renders.
 */
function mergeParentResponse(
  previous: QuestionResponseDetails | undefined,
  item: AuditQuestionItemResponse,
): QuestionResponseDetails | undefined {
  const polledMs = normalizeToTimestamp(item.respondedAt);
  const previousMs = previous?.respondedAt
    ? normalizeToTimestamp(previous.respondedAt)
    : 0;
  if (polledMs <= previousMs) return previous;
  return buildParentResponseFromItem(previous, item, polledMs);
}

/**
 * Overlay the wire row's parent-level fields ONTO the previous blob.
 *
 * Critically this does NOT rebuild the blob from scratch. The summary
 * endpoint and `ListAuditQuestionResponses` do not carry the same field
 * set: the summary is served with server-DERIVED aggregates — notably
 * the parent's `responseResult`, which the backend computes from the
 * child answers on a sub-question layout — that the raw primary-table
 * row simply doesn't have. Rebuilding from the row therefore wrote
 * `undefined` over a perfectly good answer, and that is what made the
 * NA-reason picker disappear the moment a save landed: both
 * {@link shouldShowParentNaReasonPicker} and
 * {@link shouldShowSubNaReasonPicker} hide themselves unless the result
 * is still `NA`.
 *
 * The rule is therefore: absence on the wire means "this endpoint has
 * nothing to say about that field", NEVER "clear it". A field is
 * overwritten only when the row actually carried a value.
 *
 * Tradeoff, taken deliberately: a value CLEARED in another tab stays on
 * screen in this tab until the next full summary refetch, because the
 * clear arrives as an absence we now preserve. Same-tab clears are
 * unaffected — the mutation's optimistic patch has already written the
 * cleared value locally, so preserving what's there preserves the
 * clear. A briefly stale comment is a far smaller harm than silently
 * blanking the auditor's answer, which is what the previous shape did.
 */
function buildParentResponseFromItem(
  previous: QuestionResponseDetails | undefined,
  item: AuditQuestionItemResponse,
  respondedAtMs: number,
): QuestionResponseDetails {
  return {
    // Base is what we ALREADY hold. Every field below is a conditional
    // overwrite, so a field the wire didn't send survives untouched.
    // This spread is the whole fix.
    ...(previous ?? {}),
    // The one field always taken from the wire — the gate above already
    // proved it is newer than what we hold.
    respondedAt: new Date(respondedAtMs),
    ...(item.responseStatus !== undefined ? { responseStatus: item.responseStatus } : {}),
    ...(item.responseResult !== undefined ? { responseResult: item.responseResult } : {}),
    ...(item.responseScore !== undefined ? { responseScore: item.responseScore } : {}),
    ...(item.respondedBy !== undefined ? { respondedBy: item.respondedBy } : {}),
    ...(item.response?.comment !== undefined
      ? { responseComment: item.response.comment }
      : {}),
    // Only the LABEL crosses to the SDK read shape —
    // `QuestionResponseDetails` carries no `naReasonId` (that field
    // exists on the write shapes only). The picker recovers the id by
    // matching this label against the loaded reasons list, so the label
    // is the load-bearing field here.
    ...(item.response?.naReason !== undefined
      ? { naReason: item.response.naReason }
      : {}),
    ...(item.response?.mediaIds !== undefined
      ? { mediaIds: item.response.mediaIds }
      : {}),
    ...(item.response?.taskIds !== undefined ? { taskIds: item.response.taskIds } : {}),
  } as unknown as QuestionResponseDetails;
}

/**
 * Merge the sub-response array. Each sub is independently gated by
 * its own `respondedAt` — a polled SQ1 that's newer wins even if
 * cached SQ2 is fresher than polled SQ2. Return-by-reference when
 * no sub changed.
 */
function mergeSubResponses(
  previous: readonly SubQuestionResponseEntry[],
  polledSubs: readonly SubResponseFromWire[] | undefined,
): readonly SubQuestionResponseEntry[] {
  // Absent and empty are the same thing here: the wire omits the key
  // entirely for a flat question. Guarding in this ONE place covers
  // both primary-table read paths — the page overlay and the polling
  // cache merger both funnel through it.
  if (!polledSubs || polledSubs.length === 0) return previous;

  const polledBySubId = new Map<string, SubResponseFromWire>(
    polledSubs.map((s) => [s.subQuestionId, s]),
  );

  let anyChanged = false;
  // Start from the cached list so we preserve subs that weren't in
  // the poll (defence: shouldn't happen for the same auditId, but
  // keeps the invariant that we NEVER drop a cached sub).
  const seenIds = new Set<string>();
  const nextByIndex = previous.map((entry) => {
    seenIds.add(entry.subQuestionId);
    const polled = polledBySubId.get(entry.subQuestionId);
    if (!polled) return entry;
    const merged = mergeOneSubResponse(entry, polled);
    if (merged !== entry) anyChanged = true;
    return merged;
  });

  // Sub-responses the poll returned but the cache didn't have yet —
  // append them at the end. Only build entries whose polled
  // `respondedAt` is > 0 (skip seed/placeholder rows).
  const additions: SubQuestionResponseEntry[] = [];
  for (const polled of polledSubs) {
    if (seenIds.has(polled.subQuestionId)) continue;
    const respondedAtMs = normalizeToTimestamp(polled.respondedAt);
    if (respondedAtMs <= 0) continue;
    additions.push(
      withErtFields(
        {
          subQuestionId: polled.subQuestionId,
          // No previous entry for a sub the cache hasn't seen yet.
          response: buildSubResponse(undefined, polled, respondedAtMs),
        },
        polled,
      ),
    );
    anyChanged = true;
  }

  if (!anyChanged) return previous;
  return additions.length > 0 ? [...nextByIndex, ...additions] : nextByIndex;
}

function mergeOneSubResponse(
  previous: SubQuestionResponseEntry,
  polled: SubResponseFromWire,
): SubQuestionResponseEntry {
  const polledMs = normalizeToTimestamp(polled.respondedAt);
  const previousMs = previous.response?.respondedAt
    ? normalizeToTimestamp(previous.response.respondedAt)
    : 0;
  if (polledMs <= previousMs) return previous;
  return withErtFields(
    {
      ...previous,
      response: buildSubResponse(previous.response, polled, polledMs),
    },
    polled,
  );
}

/**
 * ERT: mirror the polled row's data-collection type + value onto the
 * entry. The polled row is newer than the cached one (callers gate on
 * `respondedAt`), so it is authoritative INCLUDING absence — a value
 * cleared server-side must be dropped from the cache, not preserved
 * from the stale entry. Without this the merger silently discarded
 * `responseValue`, so a polled data-only answer would disappear.
 */
function withErtFields(
  entry: SubQuestionResponseEntry,
  polled: SubResponseFromWire,
): SubQuestionResponseEntry {
  const next: SubQuestionResponseEntry = { ...entry };
  if (polled.responseType !== undefined) {
    next.responseType = polled.responseType as ResponseType;
  } else {
    delete next.responseType;
  }
  if (polled.responseValue !== undefined && polled.responseValue !== '') {
    next.responseValue = polled.responseValue;
  } else {
    delete next.responseValue;
  }
  return next;
}

/**
 * Overlay a polled sub-response's fields onto the previous entry's
 * response blob. Same "absence never blanks" rule as
 * {@link buildParentResponseFromItem} — see its docblock for why.
 *
 * `previous` is undefined for a sub the cache hasn't seen yet, in which
 * case this degrades to building from the wire alone.
 */
function buildSubResponse(
  previous: QuestionResponseDetails | undefined,
  polled: SubResponseFromWire,
  respondedAtMs: number,
): QuestionResponseDetails {
  return {
    ...(previous ?? {}),
    respondedAt: new Date(respondedAtMs),
    ...(polled.status !== undefined ? { responseStatus: polled.status } : {}),
    ...(polled.result !== undefined ? { responseResult: polled.result } : {}),
    ...(polled.score !== undefined ? { responseScore: polled.score } : {}),
    ...(polled.respondedBy !== undefined ? { respondedBy: polled.respondedBy } : {}),
    ...(polled.comment !== undefined ? { responseComment: polled.comment } : {}),
    // Label only — see buildParentResponseFromItem: the SDK read shape
    // has no naReasonId, and the picker recovers the id from the label.
    ...(polled.naReason !== undefined ? { naReason: polled.naReason } : {}),
    ...(polled.mediaIds !== undefined ? { mediaIds: polled.mediaIds } : {}),
    ...(polled.taskIds !== undefined ? { taskIds: polled.taskIds } : {}),
  } as unknown as QuestionResponseDetails;
}
